import { TokenMeta } from "../types/LandToken";
import { toHectares } from "./formatValue";

const parseSizeToHectares = (size?: string): number => {
  if (!size) {
    return 0;
  }

  const [rawValue, rawUnit] = size.split(",");
  const value = Number(rawValue);
  const unit = String(rawUnit || "")
    .trim()
    .toLowerCase();

  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }

  if (unit.startsWith("hectare") || unit === "ha") {
    return value;
  }

  if (unit.startsWith("acre")) {
    return value * 0.404686;
  }

  if (unit === "sqm" || unit === "m2" || unit === "squaremeter" || unit === "squaremeters") {
    return value / 10000;
  }

  if (unit === "sqft" || unit === "ft2" || unit === "squarefeet") {
    return value / 107639.104;
  }

  return value;
};

/** A land's size as "value,unit": the sum of its zones when it has any, else meta.size. */
export const getEffectiveSizeFromMeta = (meta?: TokenMeta): string => {
  const zones = meta?.zones;
  if (Array.isArray(zones) && zones.length > 0) {
    const totalHectares = zones.reduce((sum, zone) => sum + parseSizeToHectares(zone.size), 0);
    return `${Number(totalHectares.toFixed(4))},hectares`;
  }
  return meta?.size || "0,hectares";
};

/** Highest zoom a single-land focus may reach; also the Explore map's maxZoom. */
export const LAND_MAX_ZOOM = 18.42;
// Lowest zoom a land focus settles at. Stays clear of Explore's `currentZoom <= 10`
// marker-size bucket, so focusing a very large land never shrinks the markers mid-fly.
const LAND_MIN_FOCUS_ZOOM = 12;
// Web Mercator: equatorial circumference (m) and mapbox-gl's tile size (px).
const EARTH_CIRCUMFERENCE_M = 40075016.686;
const TILE_PX = 512;

/**
 * The zoom at which a land fills about `targetPx` of the screen. Lands carry an area but no
 * outline, so the land is treated as a square of that area: at zoom z one pixel covers
 * C·cos(lat) / (512·2^z) metres, so its side spans targetPx at
 * z = log2(C·cos(lat)·targetPx / (512·side)). Clamped to [12, 18.42]; null when the land has
 * no usable size, so the caller keeps its default.
 */
export const landFocusZoom = (meta: TokenMeta | null | undefined, lat: number, targetPx: number): number | null => {
  const hectares = parseSizeToHectares(getEffectiveSizeFromMeta(meta ?? undefined));
  if (!(hectares > 0) || !Number.isFinite(lat) || !(targetPx > 0)) {
    return null;
  }
  const sideM = Math.sqrt(hectares * 10000);
  const zoom = Math.log2((EARTH_CIRCUMFERENCE_M * Math.cos((lat * Math.PI) / 180) * targetPx) / (TILE_PX * sideM));
  return Math.min(LAND_MAX_ZOOM, Math.max(LAND_MIN_FOCUS_ZOOM, zoom));
};

// meta.location can arrive as an unparsed JSON string (the rich token's first render(s) on
// LandPage, the featured-land feed) — parse defensively so a bad payload drops the place
// and coordinate states instead of throwing.
const parseLocation = (raw: unknown): Partial<TokenMeta["location"]> | null => {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const num = (v: unknown) => (v === null || v === undefined || v === "" ? NaN : Number(v));

/**
 * The rotating location line on land cards (components/StatusCycle): place, coordinates
 * (lon, lat — the Explore overlay's order), land size and elevation, each only when the
 * token carries it. A lighter token (LandPage's map preview) gives a shorter list that
 * grows once the full one lands.
 */
export const landStatusStates = (meta?: TokenMeta | null): string[] => {
  const location = parseLocation(meta?.location);
  const lon = num(location?.lon);
  const lat = num(location?.lat);
  const hectares = toHectares(getEffectiveSizeFromMeta(meta ?? undefined));
  const elevationMin = meta?.elevationMin;
  const elevationMax = meta?.elevationMax;
  return [
    [location?.name, location?.country].filter(Boolean).join(", "),
    Number.isFinite(lon) && Number.isFinite(lat) ? `${lon.toFixed(6)}°, ${lat.toFixed(6)}°` : "",
    parseFloat(hectares) > 0 ? hectares.toUpperCase() : "",
    Number.isFinite(num(elevationMin)) && Number.isFinite(num(elevationMax))
      ? `ELEVATION ${elevationMin}m - ${elevationMax}m`
      : "",
  ].filter(Boolean);
};
